import React from "react";
import PropTypes from "prop-types";
import { Select, Input } from "antd";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import Tooltip from "../Tooltip";
import SearchableLookup from "./SearchableLookup";
import { Undo2 } from "lucide-react";
import Checkbox from "antd/es/checkbox/Checkbox";

const { TextArea } = Input;

/** Show only path/key for file fields, not full presigned URL. Exported for use in view mode and modals. */
export function formatFileFieldDisplay(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) {
    try {
      const pathname = new URL(s).pathname;
      return pathname.slice(1) || s.slice(0, 60);
    } catch {
      return s.length > 60 ? s.slice(0, 60) + "…" : s;
    }
  }
  return s.length > 80 ? s.slice(0, 80) + "…" : s;
}

// DetailField component for view mode
export const DetailField = ({
  label,
  value,
  onDoubleClick,
  isEditable,
  actions,
  info,
}) => (
  <div
    className={`bg-white/70 backdrop-blur-sm p-2 rounded-lg border border-white/30 hover:border-white/50 transition-all duration-200 hover:shadow-md ${
      isEditable ? "cursor-pointer" : ""
    }`}
    onDoubleClick={isEditable ? onDoubleClick : undefined}
    title={isEditable ? "Double-click to edit" : undefined}
  >
    <div className="flex items-start gap-2 mb-2">
      <div className="text-sm font-medium text-gray-600">{label}</div>
      <div className="flex items-center gap-2">
        {info && (
          <Tooltip content={info}>
            <Info
              size={14}
              className="text-blue-500 hover:text-gray-400 flex-shrink-0"
            />
          </Tooltip>
        )}
        {actions && <div onClick={(e) => e.stopPropagation()}>{actions}</div>}
      </div>
    </div>
    <div className="break-words">{value}</div>
  </div>
);

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  onDoubleClick: PropTypes.func,
  isEditable: PropTypes.bool,
  actions: PropTypes.node,
  info: PropTypes.string,
};

// EditableField component for edit mode
export const EditableField = ({
  label,
  path,
  labelPath,
  type = "text",
  value,
  onChange,
  options = [],
  readOnly = false,
  error,
  lookupObject,
  searchParam,
  valueLabel,
  isModified = false,
  originalValue = null,
  originalLabelValue = null,
  onRevert = null,
  actions,
  info,
}) => {
  let control = null;
  if (label !== "Currently Residing US State" && label !== "List of Countries")
    options = [{ label: "--None--", value: "" }, ...options];
  else {
    options = options.slice(1, options.length);
  }

  if (readOnly) {
    control = <div className="text-gray-900 break-words">{value ?? "—"}</div>;
  } else if (type === "lookup") {
    control = (
      <SearchableLookup
        objectType={lookupObject}
        searchParam={searchParam ?? ""}
        value={value ?? ""}
        valueLabel={valueLabel || ""}
        onChange={(newId, newLabel) =>
          onChange(path, newId, labelPath, newLabel)
        }
        placeholder={`Search ${lookupObject}s...`}
      />
    );
  } else if (type === "textarea") {
    control = (
      <TextArea
        className="w-full rounded text-gray-900"
        value={value ?? ""}
        placeholder="Enter text..."
        onChange={(e) => onChange(path, e.target.value)}
        rows={4}
      />
    );
  } else if (type === "file") {
    const fileDisplay = formatFileFieldDisplay(value);
    control = (
      <div className="relative min-h-[80px]">
        <TextArea
          className="w-full rounded text-gray-900 pr-24"
          value={fileDisplay}
          placeholder="S3 key or file path..."
          onChange={(e) => onChange(path, e.target.value)}
          rows={3}
        />
        {actions && (
          <div
            className="absolute top-2 right-2 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {actions}
          </div>
        )}
      </div>
    );
  } else if (type === "select") {
    control = (
      <Select
        className="w-full rounded text-gray-900"
        value={value ?? ""}
        placeholder="Select an option"
        onChange={(selectedValues) => onChange(path, selectedValues)}
      >
        {options.map((opt) => {
          // Options must be objects with label and value properties
          return (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          );
        })}
      </Select>
    );
  } else if (type === "multi-select") {
    const arrayValue = Array.isArray(value)
      ? value
      : typeof value === "string" && value
        ? value.split(";")
        : [];
    control = (
      <Select
        mode="multiple"
        allowClear
        className="w-full rounded text-gray-900"
        value={arrayValue}
        placeholder="Select Options"
        onChange={(selectedValues) => onChange(path, selectedValues)}
      >
        {options.map((opt) => {
          return (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          );
        })}
      </Select>
    );
  } else if (type === "number") {
    control = (
      <Input
        type="number"
        className="w-full rounded text-gray-900"
        value={value ?? ""}
        onChange={(e) =>
          onChange(path, e.target.value === "" ? "" : Number(e.target.value))
        }
        placeholder="—"
      />
    );
  } else if (type === "date") {
    control = (
      <Input
        type="date"
        className="w-full rounded text-gray-900"
        value={value ?? ""}
        onChange={(e) => onChange(path, e.target.value)}
      />
    );
  } else if (type === "checkbox") {
    control = (
      <label className="inline-flex items-center gap-2">
        <Checkbox
          checked={Boolean(value)}
          className="pt-5 h-4 w-4"
          onChange={(e) => onChange(path, e.target.checked)}
        />
      </label>
    );
  } else {
    control = (
      <Input
        type="text"
        className="w-full rounded text-gray-900"
        value={value ?? ""}
        onChange={(e) => onChange(path, e.target.value)}
        placeholder="—"
      />
    );
  }

  return (
    <div
      className={`bg-white/70 backdrop-blur-sm p-2 rounded-lg border transition-all duration-200 ${
        isModified
          ? "border-blue-400 bg-blue-50/50 ring-2 ring-blue-200"
          : "border-white/30 hover:border-white/50"
      }`}
    >
      <div className="flex items-center gap-2 mb-0">
        <div className="text-sm font-medium text-gray-600">
          {label}
          {isModified && (
            <span className="ml-2 text-xs text-blue-600 font-semibold">
              (Modified)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {info && (
            <Tooltip content={info}>
              <Info
                size={14}
                className="text-blue-500 hover:text-gray-400 flex-shrink-0"
              />
            </Tooltip>
          )}
          {type !== "file" && actions ? (
            <div onClick={(e) => e.stopPropagation()}>{actions}</div>
          ) : null}
          {isModified && onRevert && (
            <button
              type="button"
              onClick={() => onRevert(path, originalValue)}
              className="text-xs text-gray-600 hover:text-blue-600 flex items-center gap-1 transition-colors"
              title={`Revert to original: ${originalValue ?? "—"}`}
            >
              <Undo2 size={14} />
              Revert
            </button>
          )}
        </div>
      </div>
      {control}
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
};

EditableField.propTypes = {
  label: PropTypes.string.isRequired,
  path: PropTypes.string,
  labelPath: PropTypes.string,
  type: PropTypes.oneOf([
    "text",
    "textarea",
    "file",
    "select",
    "multi-select",
    "lookup",
    "number",
    "date",
    "checkbox",
  ]),
  value: PropTypes.any,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  readOnly: PropTypes.bool,
  error: PropTypes.string,
  lookupObject: PropTypes.string,
  valueLabel: PropTypes.string,
  isModified: PropTypes.bool,
  originalLabelValue: PropTypes.any,
  originalValue: PropTypes.any,
  onRevert: PropTypes.func,
  actions: PropTypes.node,
  info: PropTypes.string,
};

// Helper function to get nested value by path
export function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length; i += 1) {
    const key = parts[i];
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[key];
  }
  return cur;
}

// Helper function to set nested value by path immutably
export function setByPathCloned(obj, path, value) {
  const parts = path.split(".");
  const root = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = root;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    const next = cur[key];
    cur[key] = next && typeof next === "object" ? { ...next } : {};
    cur = cur[key];
  }
  cur[parts[parts.length - 1]] = value;
  return root;
}

// Main field renderer function
export function renderGrid(
  fields,
  {
    editing = false,
    form = null,
    onChange,
    errors = {},
    onEnableEdit,
    gridClassName = "grid grid-cols-1 md:grid-cols-3 gap-3",
    originalData = null,
    searchParams = {},
  } = {},
) {
  const isSearchParamEmpty = Object.keys(searchParams).length === 0;
  return (
    <div className={gridClassName}>
      {fields.map((f) => {
        const key = f.label;
        const actions =
          typeof f.actions === "function"
            ? f.actions({ field: f, editing, form, originalData })
            : f.actions;

        // Edit mode rendering
        if (editing && f.path && !f.readOnly) {
          const val = getByPath(form, f.path);
          const originalVal = getByPath(originalData, f.path);
          const originalLabelValue = f.labelPath
            ? getByPath(originalData, f.labelPath)
            : undefined;
          // console.group("editable field render: ", f.path, val, originalVal);
          const err = errors[f.path];

          // Check if the value has been modified
          const isModified = val != originalVal;

          console.groupEnd();

          return (
            <EditableField
              key={key}
              label={f.label}
              path={f.path}
              labelPath={f.labelPath}
              type={f.type}
              value={val}
              onChange={onChange}
              error={err}
              options={f.options}
              readOnly={f.readOnly}
              lookupObject={f.lookupObject}
              searchParam={{
                projectId: searchParams?.projectId || f.projectId,
                projectObjectiveId:
                  searchParams?.projectObjectiveId || f.projectObjectiveId,
              }}
              valueLabel={f.valueLabel}
              isModified={isModified}
              originalValue={originalVal}
              originalLabelValue={originalLabelValue}
              onRevert={onChange}
              actions={actions}
              info={f.info}
            />
          );
        }

        // View mode rendering
        let displayValue = f.value;
        if (f.path) {
          const derived = getByPath(form || {}, f.path);
          if (derived !== undefined) displayValue = derived;
        }

        // Handle boolean values
        if (typeof displayValue === "boolean") {
          displayValue = <Checkbox checked={displayValue} disabled={true} />;
        }

        // Handle lookup fields - automatically create links
        if (f.type === "lookup" && f.lookupObject) {
          if (f.linkTo) {
            displayValue = <Link to={f.linkTo}>{f.valueLabel}</Link>;
          } else {
            displayValue = f.valueLabel;
          }
        }

        // File fields: show only path/key, not full presigned URL
        if (f.type === "file" && typeof displayValue === "string") {
          displayValue = formatFileFieldDisplay(displayValue);
        }

        // Check if field is editable (has path and is not readonly)
        const isEditable = !editing && f.path && !f.readOnly && onEnableEdit;

        return (
          <DetailField
            key={key}
            label={f.label}
            value={displayValue ?? "—"}
            onDoubleClick={onEnableEdit}
            isEditable={isEditable}
            actions={actions}
            info={f.info}
          />
        );
      })}
    </div>
  );
}

renderGrid.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
      type: PropTypes.string,
      value: PropTypes.any,
      options: PropTypes.array,
      readOnly: PropTypes.bool,
      lookupObject: PropTypes.string,
      valueLabel: PropTypes.string,
      linkTo: PropTypes.string,
      info: PropTypes.string,
    }),
  ).isRequired,
  editing: PropTypes.bool,
  form: PropTypes.object,
  onChange: PropTypes.func,
  errors: PropTypes.object,
  onEnableEdit: PropTypes.func,
  originalData: PropTypes.object,
};

// Helper function to build minimal patch for updates
export function buildMinimalPatch(original, updated) {
  if (original === updated) return undefined;
  if (updated == null || typeof updated !== "object") return updated;
  if (Array.isArray(updated)) {
    if (!Array.isArray(original)) return updated;
    if (original.length !== updated.length) return updated;
    for (let i = 0; i < updated.length; i += 1) {
      if (original[i] !== updated[i]) return updated;
    }
    return undefined;
  }
  const result = {};
  const keys = Object.keys(updated);
  for (const k of keys) {
    const diff = buildMinimalPatch(
      original ? original[k] : undefined,
      updated[k],
    );
    if (diff !== undefined) result[k] = diff;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}
